import { MaybeRefOrGetter, shallowReactive, toValue } from "vue";

import type { Link, Locator } from "@readium/shared";

import type { Direction, Navigator, TocItem } from "./types";

export class NavigatorHistory {
  private previousPositions = shallowReactive<Locator[]>([]);
  private forwardPositions = shallowReactive<Locator[]>([]);

  constructor(private readonly nav: MaybeRefOrGetter<Navigator>) {}

  async go(destination: Direction | Locator | Link | number): Promise<void> {
    const previous = this.raw.activeLocator;
    await this.raw.go(destination);
    this.previousPositions.push(previous);
  }

  get hasBack(): boolean {
    return this.previousPositions.length > 0;
  }

  get hasForward(): boolean {
    return this.forwardPositions.length > 0;
  }

  async goBack(): Promise<void> {
    if (!this.previousPositions.length) return;

    const currentLocator = this.raw.activeLocator;
    this.forwardPositions.push(currentLocator);

    await this.raw.go(this.previousPositions.pop());
  }

  async goForward(): Promise<void> {
    if (!this.forwardPositions.length) return;

    const currentLocator = this.raw.activeLocator;
    this.previousPositions.push(currentLocator);

    await this.raw.go(this.forwardPositions.pop());
  }

  get raw(): Navigator {
    return toValue(this.nav);
  }

  // forward methods to the original navigator
  get activeLocator(): Locator {
    return this.raw?.activeLocator;
  }
  get positionIndicator(): string {
    return this.raw?.positionIndicator;
  }
  get positions() {
    return this.raw?.positions || [];
  }
  get tableOfContents(): Readonly<TocItem[]> {
    return this.raw?.tableOfContents;
  }
}
